import type { Components, Theme } from "@mui/material/styles";
import { FONT_MONO } from "./typography";

/** App-wide MUI component defaults + overrides. Borders over elevation. */
export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      body: { fontSynthesis: "none", textRendering: "optimizeLegibility" },
      "::selection": { backgroundColor: "rgba(79,70,229,0.18)" },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true, size: "small" },
    styleOverrides: {
      root: { borderRadius: 8, paddingInline: 14 },
      sizeSmall: { paddingBlock: 5 },
    },
    // The dark-scheme brand colour is lightened for legible text/accents, which
    // leaves it too pale behind white button text. Use the darker shade for
    // filled brand buttons in dark mode so the label clears WCAG AA.
    variants: [
      {
        props: { variant: "contained", color: "primary" },
        style: ({ theme }) => ({
          ...theme.applyStyles("dark", {
            backgroundColor: (theme.vars ?? theme).palette.primary.dark,
            "&:hover": { backgroundColor: (theme.vars ?? theme).palette.primary.main },
          }),
        }),
      },
      {
        props: { variant: "contained", color: "secondary" },
        style: ({ theme }) => ({
          ...theme.applyStyles("dark", {
            backgroundColor: (theme.vars ?? theme).palette.secondary.dark,
            "&:hover": { backgroundColor: (theme.vars ?? theme).palette.secondary.main },
          }),
        }),
      },
    ],
  },
  MuiIconButton: {
    defaultProps: { size: "small" },
  },
  MuiTextField: {
    defaultProps: { size: "small", variant: "outlined" },
  },
  MuiOutlinedInput: {
    styleOverrides: { root: { borderRadius: 8 } },
  },
  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        border: `1px solid ${(theme.vars ?? theme).palette.divider}`,
        borderRadius: 12,
        backgroundImage: "none",
      }),
    },
  },
  MuiPaper: {
    styleOverrides: { root: { backgroundImage: "none" } },
  },
  MuiChip: {
    defaultProps: { size: "small" },
    styleOverrides: {
      root: { fontWeight: 600, borderRadius: 7 },
      label: { paddingInline: 8 },
    },
  },
  MuiTooltip: {
    defaultProps: { arrow: true },
    styleOverrides: { tooltip: { fontSize: "0.75rem" } },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: (theme.vars ?? theme).palette.divider,
        paddingBlock: 10,
      }),
      head: ({ theme }) => ({
        fontWeight: 700,
        fontSize: "0.6875rem",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: (theme.vars ?? theme).palette.text.secondary,
        backgroundColor: (theme.vars ?? theme).palette.background.paper,
      }),
    },
  },
  MuiTab: {
    styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 44 } },
  },
  MuiDialog: {
    styleOverrides: { paper: { borderRadius: 14 } },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({ border: `1px solid ${(theme.vars ?? theme).palette.divider}` }),
    },
  },
  MuiLink: {
    defaultProps: { underline: "hover" },
  },
  MuiAlert: {
    styleOverrides: { root: { borderRadius: 10 } },
  },
  MuiSkeleton: {
    defaultProps: { animation: "wave" },
  },
};

/** Class applied to elements that should render in the monospace face. */
export const MONO_SX = { fontFamily: FONT_MONO } as const;
